import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Navbar } from './common/Catalyst/navbar';
import AppSidebar from './AppSidebar';
import { Heading, Subheading } from './common/Catalyst/heading';
import { Divider } from './common/Catalyst/divider';
import { Button } from './common/Catalyst/button';
import LoadingSpinner from './LoadingSpinner';
import ShowAlert from './ShowAlert';
import BillingTabs, { BillingTab } from './billing/BillingTabs';
import HomeOverrideList from './billing/HomeOverrideList';
import HomeAccountPanel from './billing/HomeAccountPanel';
import HomePaymentList from './billing/HomePaymentList';
import StatementPanel from './billing/StatementPanel';
import CouponPanel from './billing/CouponPanel';
import EstoppelPanel from './billing/EstoppelPanel';
import TenureList from './billing/TenureList';
import { HomeReturn } from './clients/rust-api-client';
import { homeService } from './clients/ApiClients';
import { useSyncedHome } from './sync/hooks';
import { billingErrorMessage } from './utils/billingHelpers';
import { getUserTypeFromToken } from './utils/TokenUtils';
import {
  canManageSchedules,
  canPostCharge,
  canPostCredit,
  canIssueEstoppel,
  canManageTenures,
  canRecordPayment,
  canReverseEntry,
  canViewLedger,
} from './constants/UserTypes';

/**
 * Which screen of a home's account to show.
 *
 * These are the *per-home* half of billing — the ledger and everything
 * derived from it. Policy and process (settings, dues, runs, payments,
 * collections) are per-HOA and live on `BillingPage`.
 */
export type HomeBillingSection =
  | 'account'
  | 'statement'
  | 'payments'
  | 'tenures'
  | 'estoppel';

interface Props {
  section: HomeBillingSection;
}

const HomeBillingPage: React.FC<Props> = ({ section }) => {
  const { hoaId, homeId } = useParams<{ hoaId: string; homeId: string }>();
  const navigate = useNavigate();
  const userType = useMemo(() => getUserTypeFromToken(), []);

  const [fetchedHome, setHome] = useState<HomeReturn | null>(null);
  // The sync engine's copy when it has this home (#561); fetched otherwise.
  const syncedHome = useSyncedHome(homeId);
  const haveSynced = !!syncedHome;
  const home = syncedHome ?? fetchedHome;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHome = async () => {
      if (!homeId) return;
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const { data } = await homeService.getHome({ id: homeId });
        setHome(data);
      } catch (err) {
        console.error('Error fetching home:', err);
        setError(billingErrorMessage(err, "Couldn't load this home."));
      } finally {
        setLoading(false);
      }
    };
    void fetchHome();
  }, [homeId, haveSynced]);

  const base = `/hoa/${hoaId}/home/${homeId}/billing`;
  const tabs: BillingTab[] = [
    { to: base, label: 'Account' },
    { to: `${base}/statement`, label: 'Statement & coupon' },
    { to: `${base}/payments`, label: 'Payments' },
    { to: `${base}/tenures`, label: 'Ownership' },
    { to: `${base}/estoppel`, label: 'Estoppel' },
  ];

  const body = (): React.ReactNode => {
    switch (section) {
      case 'statement':
        return hoaId && homeId ? (
          <>
            <StatementPanel homeId={homeId} />
            <Divider className="my-10" />
            <CouponPanel homeId={homeId} />
          </>
        ) : null;
      case 'payments':
        return hoaId && homeId ? (
          <HomePaymentList
            hoaId={hoaId}
            homeId={homeId}
            canRecord={canRecordPayment(userType)}
            canRefund={canPostCredit(userType)}
            canReturn={canReverseEntry(userType)}
          />
        ) : null;
      case 'tenures':
        return homeId ? (
          <TenureList homeId={homeId} canManage={canManageTenures(userType)} />
        ) : null;
      case 'estoppel':
        return hoaId && homeId ? (
          <EstoppelPanel
            hoaId={hoaId}
            homeId={homeId}
            canIssue={canIssueEstoppel(userType)}
          />
        ) : null;
      case 'account':
      default:
        return hoaId && homeId ? (
          <>
            <HomeAccountPanel
              hoaId={hoaId}
              homeId={homeId}
              canPostCharge={canPostCharge(userType)}
              canPostCredit={canPostCredit(userType)}
              canReverse={canReverseEntry(userType)}
            />
            <div className="mt-10">
              <HomeOverrideList
                hoaId={hoaId}
                homeId={homeId}
                canManage={canManageSchedules(userType)}
              />
            </div>
          </>
        ) : null;
    }
  };

  if (!canViewLedger(userType)) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage="hoas" />}
        navbar={<Navbar>{/* Your navbar content */}</Navbar>}
      >
        <Heading level={2}>Billing</Heading>
        <Subheading className="mt-2">
          You do not have access to this home's account.
        </Subheading>
      </SidebarLayout>
    );
  }

  const address = home
    ? [home.street, home.street2].filter(Boolean).join(' ')
    : '';

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="hoas" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="flex justify-between items-center">
        <div>
          <Heading level={2}>
            {address ? `${address} — Account` : 'Account'}
          </Heading>
          <Subheading className="mt-1">
            This home's ledger and everything derived from it.
          </Subheading>
        </div>
        {hoaId && homeId && (
          <Button
            plain
            onClick={() => navigate(`/hoa/${hoaId}/home/${homeId}`)}
          >
            Back to the home
          </Button>
        )}
      </div>

      <Divider className="mt-4 mb-6" />

      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load this home"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        </div>
      )}

      <BillingTabs tabs={tabs} />

      {loading ? <LoadingSpinner /> : body()}
    </SidebarLayout>
  );
};

export default HomeBillingPage;
